import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, AlertTriangle, FileText } from "lucide-react";
import { serpAnalysisApi, type SeoCompetitor } from "@/api/serp-analysis";

// ── Score badge color ──

function scoreBadge(score: number | null) {
  if (score === null) return <span className="text-muted-foreground text-xs">-</span>;
  const rounded = Math.round(score);
  const color =
    rounded >= 80
      ? "bg-emerald-600 text-white"
      : rounded >= 60
      ? "bg-amber-500 text-white"
      : "bg-red-500 text-white";
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold ${color}`}>
      {rounded}
    </span>
  );
}

function checkMark(val: boolean | null) {
  if (val === null) return <span className="text-muted-foreground text-xs">-</span>;
  return val ? (
    <span className="text-emerald-500 font-bold text-sm">✓</span>
  ) : (
    <span className="text-red-400 text-sm">✗</span>
  );
}

function truncate(text: string | null, maxLen: number) {
  if (!text) return <span className="text-muted-foreground text-xs italic">—</span>;
  return text.length > maxLen ? (
    <span title={text}>{text.slice(0, maxLen)}…</span>
  ) : (
    <span>{text}</span>
  );
}

function rangeHint(values: number[], label: string) {
  const valid = values.filter((v) => v > 0);
  if (valid.length === 0) return null;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const median = valid.sort((a, b) => a - b)[Math.floor(valid.length / 2)];
  return (
    <p className="text-xs text-muted-foreground mt-1">
      {label}：{min}~{max} 字（競品中位數 {median}）
    </p>
  );
}

// ── Section 1: Title Analysis ──

function TitleSection({ competitors, keyword }: { competitors: SeoCompetitor[]; keyword: string }) {
  const titleLengths = competitors.map((c) => c.title?.length ?? 0);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">1. Title Analysis — 標題分析</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">競品域名</TableHead>
                <TableHead>標題 Title</TableHead>
                <TableHead className="w-16 text-right">字數</TableHead>
                <TableHead className="w-20 text-center">含關鍵字</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {competitors.map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs text-muted-foreground truncate max-w-[128px]">
                    {c.domain}
                  </TableCell>
                  <TableCell className="text-xs max-w-[400px]">
                    {c.title ? (
                      <span
                        className={
                          keyword &&
                          c.title.toLowerCase().includes(keyword.toLowerCase())
                            ? "text-amber-400"
                            : ""
                        }
                        title={c.title}
                      >
                        {c.title.length > 80 ? `${c.title.slice(0, 80)}…` : c.title}
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs font-mono">
                    {c.title?.length ?? "—"}
                  </TableCell>
                  <TableCell className="text-center">{checkMark(c.keyword_in_title)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 pb-3">{rangeHint(titleLengths, "建議標題長度")}</div>
      </CardContent>
    </Card>
  );
}

// ── Section 2: Description Analysis ──

function DescSection({ competitors }: { competitors: SeoCompetitor[] }) {
  const descLengths = competitors.map((c) => c.meta_description?.length ?? 0);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">2. Description Analysis — Meta 描述分析</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">競品域名</TableHead>
                <TableHead>Meta Description</TableHead>
                <TableHead className="w-16 text-right">字數</TableHead>
                <TableHead className="w-20 text-center">含關鍵字</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {competitors.map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs text-muted-foreground truncate max-w-[128px]">
                    {c.domain}
                  </TableCell>
                  <TableCell className="text-xs max-w-[400px]">
                    {truncate(c.meta_description, 120)}
                  </TableCell>
                  <TableCell className="text-right text-xs font-mono">
                    {c.meta_description?.length ?? "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    {checkMark(c.keyword_in_description)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 pb-3">{rangeHint(descLengths, "建議 Description 長度")}</div>
      </CardContent>
    </Card>
  );
}

// ── Section 3: H-Tags Overview ──

function HTagsSection({ competitors }: { competitors: SeoCompetitor[] }) {
  const h2Values = competitors.map((c) => c.h2_count).filter((v) => v > 0);
  const h3Values = competitors.map((c) => c.h3_count).filter((v) => v > 0);

  function minMax(vals: number[]) {
    if (vals.length === 0) return "—";
    return `${Math.min(...vals)}~${Math.max(...vals)}`;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">3. H-Tags Overview — 標題標籤總覽</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">競品域名</TableHead>
                <TableHead className="w-16 text-center">H1 數</TableHead>
                <TableHead className="w-16 text-center">H2 數</TableHead>
                <TableHead className="w-16 text-center">H3 數</TableHead>
                <TableHead className="w-24 text-right">字數</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {competitors.map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs text-muted-foreground truncate max-w-[128px]">
                    {c.domain}
                  </TableCell>
                  <TableCell className="text-center text-xs font-mono">{c.h1_count}</TableCell>
                  <TableCell className="text-center text-xs font-mono">{c.h2_count}</TableCell>
                  <TableCell className="text-center text-xs font-mono">{c.h3_count}</TableCell>
                  <TableCell className="text-right text-xs font-mono">
                    {c.word_count.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="px-4 pb-3 text-xs text-muted-foreground mt-1">
          建議 H2：{minMax(h2Values)} 個 | 建議 H3：{minMax(h3Values)} 個
        </p>
      </CardContent>
    </Card>
  );
}

// ── Section 4: Images Analysis ──

function ImagesSection({ competitors }: { competitors: SeoCompetitor[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">4. Images Analysis — 圖片分析</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">競品域名</TableHead>
                <TableHead className="w-20 text-center">圖片數</TableHead>
                <TableHead className="w-28 text-center">Schema Markup</TableHead>
                <TableHead className="w-20 text-center">可索引</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {competitors.map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs text-muted-foreground truncate max-w-[128px]">
                    {c.domain}
                  </TableCell>
                  <TableCell className="text-center text-xs font-mono">{c.images_count}</TableCell>
                  <TableCell className="text-center">{checkMark(c.has_schema_markup)}</TableCell>
                  <TableCell className="text-center">{checkMark(c.indexable)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Section 5: SEO Scores ──

function ScoresSection({ competitors }: { competitors: SeoCompetitor[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">5. SEO Scores — SEO 評分</CardTitle>
        <p className="text-xs text-muted-foreground">
          <span className="inline-block px-1.5 rounded bg-emerald-600 text-white text-xs mr-1">≥80</span>良好
          <span className="inline-block px-1.5 rounded bg-amber-500 text-white text-xs mx-1">60-79</span>待改善
          <span className="inline-block px-1.5 rounded bg-red-500 text-white text-xs ml-1">&lt;60</span>需優化
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">競品域名</TableHead>
                <TableHead className="w-16 text-center">總分</TableHead>
                <TableHead className="w-16 text-center">Title</TableHead>
                <TableHead className="w-16 text-center">Desc</TableHead>
                <TableHead className="w-16 text-center">Heading</TableHead>
                <TableHead className="w-16 text-center">Content</TableHead>
                <TableHead className="w-16 text-center">Images</TableHead>
                <TableHead className="w-16 text-center">Security</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {competitors.map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs text-muted-foreground truncate max-w-[128px]">
                    {c.domain}
                  </TableCell>
                  <TableCell className="text-center">{scoreBadge(c.overall_score)}</TableCell>
                  <TableCell className="text-center">{scoreBadge(c.title_score)}</TableCell>
                  <TableCell className="text-center">{scoreBadge(c.description_score)}</TableCell>
                  <TableCell className="text-center">{scoreBadge(c.heading_score)}</TableCell>
                  <TableCell className="text-center">{scoreBadge(c.content_score)}</TableCell>
                  <TableCell className="text-center">{scoreBadge(c.images_score)}</TableCell>
                  <TableCell className="text-center">{scoreBadge(c.security_score)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Summary stats bar ──

function StatsBar({ competitors }: { competitors: SeoCompetitor[] }) {
  const withTitle = competitors.filter((c) => c.title).length;
  const withDesc = competitors.filter((c) => c.meta_description).length;
  const withScore = competitors.filter((c) => c.overall_score !== null).length;
  const avgScore = useMemo(() => {
    const scores = competitors.map((c) => c.overall_score).filter((s): s is number => s !== null);
    if (scores.length === 0) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [competitors]);

  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <Badge variant="outline">{competitors.length} 個競品</Badge>
      <Badge variant="outline">有標題 {withTitle}/{competitors.length}</Badge>
      <Badge variant="outline">有描述 {withDesc}/{competitors.length}</Badge>
      <Badge variant="outline">有 SEO 評分 {withScore}/{competitors.length}</Badge>
      {avgScore !== null && (
        <Badge variant="outline">平均總分 {avgScore}</Badge>
      )}
    </div>
  );
}

// ── Main SeoDetailsTab ──

export default function SeoDetailsTab({ requestId, keyword }: { requestId: string; keyword: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["seo-data", requestId],
    queryFn: async () => {
      const res = await serpAnalysisApi.getSeoData(requestId);
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        <p className="text-muted-foreground mt-2">載入 SEO Details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center text-red-500">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>{(error as Error).message}</p>
      </div>
    );
  }

  if (!data?.competitors?.length) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <FileText className="h-8 w-8 mx-auto mb-2" />
        <p>尚無 SEO Details 資料</p>
      </div>
    );
  }

  const kw = keyword || data.target_keyword;

  return (
    <div className="space-y-4">
      <StatsBar competitors={data.competitors} />
      <TitleSection competitors={data.competitors} keyword={kw} />
      <DescSection competitors={data.competitors} />
      <HTagsSection competitors={data.competitors} />
      <ImagesSection competitors={data.competitors} />
      <ScoresSection competitors={data.competitors} />
    </div>
  );
}
